import { ApiError, CreateNewBeneficiaryRequest, CreateNewBeneficiaryStandaloneRequest } from "@givenwell/management-api";
import { queryOptions, useMutation } from "@tanstack/react-query";
import { api } from "./utils";

export const beneficiaryQuery = (supplierId: string) => {
  return queryOptions({
    queryKey: ["suppliers", supplierId, "beneficiary"],
    queryFn: async () => {
      try {
        const res = await api.airWallex.getBeneficiary({ supplierId });
        return res;
      } catch (e: unknown) {
        if (e instanceof ApiError && e.status === 404) {
          return null;
        }
        throw e;
      }
    },
  });
};

// -- Embedded beneficiary component ---------------------------------------------------------------

/**
 * Mints a short-lived (30s, single-use) AirWallex authorization code for the embedded beneficiary
 * form. Called immediately before initialising the Components SDK; scope is fixed server-side.
 */
export function useAuthorizeAirWallexComponentMutation() {
  return useMutation({
    mutationFn: () => api.airWallexComponents.authorizeAirWallexComponent(),
  });
}

/**
 * Standalone (unauthenticated) variant for the new-supplier onboarding wizard. Currently returns 403
 * server-side — self-service signup is mothballed — so the embedded form renders but can't authorize
 * until self-signup is re-enabled with token auth. Kept wired so re-enabling is just a server change.
 */
export function useAuthorizeAirWallexComponentStandaloneMutation() {
  return useMutation({
    mutationFn: () => api.airWallexComponents.authorizeAirWallexComponentStandalone(),
  });
}

/** The currencies the beneficiary-requirements endpoints accept, per the generated client. */
export type PayerCurrency = NonNullable<
  Parameters<typeof api.airWallexComponents.getBeneficiaryRequirements>[0]["payerCurrency"]
>;

/**
 * Country-specific values the embedded beneficiary form needs to render the bank fields directly
 * (account currency + local clearing systems), so it can skip the transfer-method picker.
 *
 * `payerCurrency` is the currency the payout is funded in. It is required for a country with no
 * account currency of its own — Cambodia is paid in whatever currency funds it, because Airwallex
 * cannot settle KHR — and safe to send for the rest, which accept it when it matches their fixed
 * currency and reject it when it disagrees.
 */
export const beneficiaryRequirementsQuery = (bankCountry: string, payerCurrency?: PayerCurrency) =>
  queryOptions({
    queryKey: ["airwallex-beneficiary-requirements", bankCountry, payerCurrency],
    queryFn: () => api.airWallexComponents.getBeneficiaryRequirements({ bankCountry, payerCurrency }),
    staleTime: Infinity,
  });

/**
 * Standalone (unauthenticated) variant of the requirements query for the new-supplier onboarding
 * wizard. Currently 403s server-side (self-signup mothballed); kept wired for easy re-enable.
 */
export const beneficiaryRequirementsStandaloneQuery = (bankCountry: string, payerCurrency?: PayerCurrency) =>
  queryOptions({
    queryKey: ["airwallex-beneficiary-requirements-standalone", bankCountry, payerCurrency],
    queryFn: () => api.airWallexComponents.getBeneficiaryRequirementsStandalone({ bankCountry, payerCurrency }),
    staleTime: Infinity,
  });

// -------------------------------------------------------------------------------------------------

export function useCreateNewBeneficiaryMutation() {
  return useMutation({
    mutationFn: (args: CreateNewBeneficiaryRequest) => api.airWallex.createNewBeneficiary({ requestBody: args }),
  });
}

/**
 * Standalone (unauthenticated) beneficiary creation for the new-supplier onboarding wizard.
 * Currently 403s server-side (self-signup mothballed). Kept wired so re-enabling is a server change.
 */
export function useCreateNewBeneficiaryStandaloneMutation() {
  return useMutation({
    mutationFn: (args: CreateNewBeneficiaryStandaloneRequest) =>
      api.airWallex.createNewBeneficiaryStandalone({ requestBody: args }),
  });
}

export function useDeleteNewBeneficiaryMutation() {
  return useMutation({
    mutationFn: (args: { supplierId: string }) => api.airWallex.deleteBeneficiary({ supplierId: args.supplierId }),
  });
}
